import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {map, Observable, shareReplay, switchMap, tap} from 'rxjs';
import {User} from '../../model/user';
import {Company} from '../../model/proposal/company';
import {UserPage} from '../../model/user-page';
import {ImageReader} from '../../util/image-reader';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  profilePictures = new Map<string, Observable<any>>();

  constructor(
    private readonly http: HttpClient) {
  }

  getUserById(userId: string): Observable<User> {
    return this.http.get<User>(environment.backend + '/v1/users/' + userId)
      .pipe(map(member => this.mapUser(member)));
  }

  mapUser(user: User): User {
    const newUser = new User(user);
    newUser.company = new Company(user.company);
    return newUser;
  }

  getUserPage(userId: string): Observable<UserPage> {
    return this.http.get<UserPage>(environment.backend + `/v1/users/${userId}/page`).pipe(map(response => this.mapSocialNetUser(response)));
  }

  getUsersFromTheSameCompany(userId: string): Observable<User[]> {
    return this.http.get<User[]>(environment.backend + `/v1/users/same-company-user/${userId}`);
  }

  private mapSocialNetUser(response: UserPage): UserPage {
    const userPage = new UserPage(response);
    userPage.introductionText = response.introductionText;
    userPage.company = new Company(response.company);
    return userPage;
  }

  saveUser(user: User) {
    return this.http.post(environment.backend + '/v1/users', user);
  }

  updateEmailNotifications(userId: string, emailNotifications: boolean) {
    return this.http.patch(environment.backend + `/v1/users/${userId}/email-notifications`, {emailNotifications: emailNotifications});
  }
  updateUser(userId: string, user: User) {
    return this.http.patch(environment.backend + `/v1/users/${userId}`, user);
  }

  updateUserSkenDataCredentials(userId: string, user: User) {
    return this.http.patch(environment.backend + `/v1/users/skendata/${userId}`, user);
  }

  updateUserRepresentative(userId: string, representativeUUID: string) {
    return this.http.patch(environment.backend + `/v1/users/representative/${userId}`, representativeUUID);
  }

  updateUserProfile(userId: string, userPage: UserPage) {
    return this.http.patch(environment.backend + `/v1/users/${userId}/page`, userPage);
  }

  setProfilePicture(userId: string, image: any): Observable<any> {
    const formdata = new FormData();
    formdata.append('profilePicture', image);
    return this.http.post(environment.backend + `/v1/users/${userId}/pictures/profile`, formdata).pipe(tap(() => this.resetImages()));
  }

  deleteProfilePicture(userId: string): Observable<any> {
    return this.http.delete(environment.backend + `/v1/users/${userId}/pictures/profile`).pipe(tap(() => this.resetImages()));
  }

  getProfilePicture(userId: string): Observable<any> {
    if (this.profilePictures.has(userId)) {
      return this.profilePictures.get(userId);
    }
    const image = this.http.get(environment.backend + `/v1/users/${userId}/pictures/profile`, {responseType: 'blob'}).pipe(switchMap(value => ImageReader.blobToData(value)), shareReplay());
    this.profilePictures.set(userId, image);
    return this.profilePictures.get(userId);
  }

  setTitlePicture(userId: string, image: any): Observable<any> {
    const formdata = new FormData();
    formdata.append('titlePicture', image);
    return this.http.post(environment.backend + `/v1/users/${userId}/pictures/title`, formdata);
  }

  deleteTitlePicture(userId: string): Observable<any> {
    return this.http.delete(environment.backend + `/v1/users/${userId}/pictures/title`);
  }

  getTitlePicture(userId: string) {
    return this.http.get(environment.backend + `/v1/users/${userId}/pictures/title`, {responseType: 'blob'}).pipe(switchMap(value => ImageReader.blobToData(value)));
  }

  resetImages() {
    this.profilePictures = new Map<string, Observable<any>>();
  }

  getAllBrokerAndUnderwriter(lobNumber?): Observable<User[]> {
    if (lobNumber == null) {
      return this.http.get<User[]>(environment.backend + '/v1/users/underwriterandbrokers');
    } else {
      return this.http.get<User[]>(environment.backend + '/v1/users/underwriterandbrokers', {params: {lookingForLobNumber: lobNumber}});
    }
  }

  getUserInfoFromKeycloak(uuid: string): Observable<User> {
    return this.http.get<User>(environment.backend + `/v1/users/${uuid}/onboarding`);
  }

  getAllUsersMailings(): Observable<User[]> {
      return this.http.get<User[]>(environment.backend + '/v1/users/mailings');
  }

}
